import React, { useEffect, useMemo, useState } from "react";
import { MODAL_BACKDROP_STYLE, SMART_PREFILL_CONFIG } from "../constants";
import {
    formatKeyTestSuccessMessage,
    formatMoreModelsLabel,
    SMART_PREFILL_STRINGS
} from "../strings";

const SmartPrefillModal = ({
    isOpen,
    onClose,
    onGenerate,
    onTestKey,
    loading,
    error,
    testResult,
    testLoading
}) => {
    const [prompt, setPrompt] = useState("");
    const [apiKey, setApiKey] = useState("");
    const [rememberKey, setRememberKey] = useState(true);
    const [localError, setLocalError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            try {
                const storedKey = localStorage.getItem(SMART_PREFILL_CONFIG.apiKeyStorageKey);
                if (storedKey) {
                    setApiKey(storedKey);
                    setRememberKey(true);
                }
            } catch (storageError) {
                setRememberKey(false);
            }
            return;
        }
        setPrompt("");
        setLocalError(null);
    }, [isOpen]);

    const handleGenerate = () => {
        const trimmed = prompt.trim();
        if (!trimmed) {
            setLocalError(SMART_PREFILL_STRINGS.errorMissingPrompt);
            return;
        }
        const trimmedKey = apiKey.trim();
        if (rememberKey) {
            try {
                if (trimmedKey) {
                    localStorage.setItem(SMART_PREFILL_CONFIG.apiKeyStorageKey, trimmedKey);
                } else {
                    localStorage.removeItem(SMART_PREFILL_CONFIG.apiKeyStorageKey);
                }
            } catch (storageError) {
                setRememberKey(false);
            }
        } else {
            try {
                localStorage.removeItem(SMART_PREFILL_CONFIG.apiKeyStorageKey);
            } catch (storageError) {
                // Ignore localStorage errors
            }
        }
        setLocalError(null);
        onGenerate(trimmed, trimmedKey || undefined);
    };

    const handleTestKey = () => {
        const trimmedKey = apiKey.trim();
        if (rememberKey) {
            try {
                if (trimmedKey) {
                    localStorage.setItem(SMART_PREFILL_CONFIG.apiKeyStorageKey, trimmedKey);
                } else {
                    localStorage.removeItem(SMART_PREFILL_CONFIG.apiKeyStorageKey);
                }
            } catch (storageError) {
                setRememberKey(false);
            }
        } else {
            try {
                localStorage.removeItem(SMART_PREFILL_CONFIG.apiKeyStorageKey);
            } catch (storageError) {
                // Ignore localStorage errors
            }
        }
        setLocalError(null);
        onTestKey(trimmedKey || undefined);
    };

    const errorMessage = localError || error;
    const showTestResult = Boolean(testResult);
    const visibleModels = useMemo(() => {
        if (!testResult?.models || !Array.isArray(testResult.models)) {
            return [];
        }
        return testResult.models.slice(0, SMART_PREFILL_CONFIG.maxModelNamesToShow);
    }, [testResult]);
    const extraModelsCount = useMemo(() => {
        if (!testResult?.models || !Array.isArray(testResult.models)) {
            return 0;
        }
        return Math.max(0, testResult.models.length - visibleModels.length);
    }, [testResult, visibleModels]);

    if (!isOpen) {
        return null;
    }

    return (
        <div className="modal show d-block" style={MODAL_BACKDROP_STYLE}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{SMART_PREFILL_STRINGS.modalTitle}</h5>
                        <button type="button" className="btn-close" onClick={onClose} disabled={loading}></button>
                    </div>
                    <div className="modal-body">
                        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

                        <div className="mb-3">
                            <label className="form-label">{SMART_PREFILL_STRINGS.apiKeyLabel}</label>
                            <input
                                type="password"
                                className="form-control"
                                value={apiKey}
                                onChange={(event) => setApiKey(event.target.value)}
                                placeholder={SMART_PREFILL_STRINGS.apiKeyPlaceholder}
                                disabled={loading || testLoading}
                            />
                            <small className="text-muted">{SMART_PREFILL_STRINGS.apiKeyHelper}</small>
                            <div className="form-check mt-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={rememberKey}
                                    onChange={(event) => setRememberKey(event.target.checked)}
                                    id="rememberGeminiKey"
                                    disabled={loading || testLoading}
                                />
                                <label className="form-check-label" htmlFor="rememberGeminiKey">
                                    {SMART_PREFILL_STRINGS.rememberKeyLabel}
                                </label>
                            </div>
                            {showTestResult && (
                                <div className={`alert mt-3 ${testResult.ok ? "alert-success" : "alert-danger"}`}>
                                    <strong>{SMART_PREFILL_STRINGS.testResultLabel}</strong>
                                    <div className="mt-1">
                                        {testResult.ok
                                            ? formatKeyTestSuccessMessage(testResult.apiVersion, testResult.models.length)
                                            : testResult.message}
                                    </div>
                                    {testResult.ok && visibleModels.length > 0 && (
                                        <div className="mt-2">
                                            <div className="text-muted">{SMART_PREFILL_STRINGS.testModelsLabel}</div>
                                            <div className="d-flex flex-wrap gap-1">
                                                {visibleModels.map((model) => (
                                                    <span key={model} className="badge bg-light text-dark">
                                                        {model}
                                                    </span>
                                                ))}
                                                {extraModelsCount > 0 && (
                                                    <span className="badge bg-light text-dark">
                                                        {formatMoreModelsLabel(extraModelsCount)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">{SMART_PREFILL_STRINGS.promptLabel}</label>
                            <textarea
                                className="form-control"
                                rows={SMART_PREFILL_CONFIG.promptRows}
                                value={prompt}
                                onChange={(event) => setPrompt(event.target.value)}
                                placeholder={SMART_PREFILL_STRINGS.promptPlaceholder}
                                maxLength={SMART_PREFILL_CONFIG.maxPromptLength}
                                disabled={loading || testLoading}
                            />
                            <small className="text-muted">{SMART_PREFILL_STRINGS.helperText}</small>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                            {SMART_PREFILL_STRINGS.cancelButton}
                        </button>
                        <button type="button" className="btn btn-outline-primary" onClick={handleTestKey} disabled={loading || testLoading}>
                            {testLoading ? SMART_PREFILL_STRINGS.testingButton : SMART_PREFILL_STRINGS.testButton}
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleGenerate} disabled={loading}>
                            {loading ? SMART_PREFILL_STRINGS.generatingButton : SMART_PREFILL_STRINGS.generateButton}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SmartPrefillModal;
